import React, { useState } from 'react';
import { Clock, Search, ArrowUp, Heart, Shield, Users, Brain, AlertCircle } from 'lucide-react';

const AIEthics: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [showBackToTop, setShowBackToTop] = useState(false);

  // Handle scroll events for back-to-top button
  React.useEffect(() => {
    const handleScroll = () => {
      setShowBackToTop(window.scrollY > 300);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Scroll to top function
  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <div className="min-h-screen bg-gray-50 pt-24">
      <div className="container mx-auto px-4 md:px-6 py-12">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <h1 className="text-4xl md:text-5xl font-bold text-gray-800 mb-6 font-['Poppins']">
            AI Ethics Statement
          </h1>
          
          <div className="flex items-center gap-4 text-gray-600 mb-8">
            <div className="flex items-center">
              <Clock className="w-5 h-5 mr-2" />
              <span>8 min read</span>
            </div>
            <div className="h-1 w-1 bg-gray-300 rounded-full"></div>
            <span>Last updated: March 15, 2025</span>
          </div>

          {/* Search */}
          <div className="relative mb-12">
            <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search AI ethics statement..."
              className="w-full pl-12 pr-4 py-3 rounded-lg border border-gray-200 focus:ring-2 focus:ring-[#C2A4D6] focus:border-transparent"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>

          {/* Core Values */}
          <section className="bg-white rounded-xl shadow-md p-6 md:p-8 mb-8">
            <div className="flex items-center mb-6">
              <Heart className="w-8 h-8 text-[#F4B6A1] mr-3" />
              <h2 className="text-2xl font-semibold">Core Values</h2>
            </div>
            <p className="text-gray-600 mb-6">
              Our AI development is guided by these fundamental principles:
            </p>
            <ul className="list-disc pl-5 space-y-3 text-gray-600">
              <li>Prioritize human emotional wellbeing above all else</li>
              <li>Maintain transparency about AI capabilities and limitations</li>
              <li>Respect user privacy and confidentiality</li>
            </ul>
          </section>

          {/* User Safety */}
          <section className="bg-white rounded-xl shadow-md p-6 md:p-8 mb-8">
            <div className="flex items-center mb-6">
              <Shield className="w-8 h-8 text-[#C2A4D6] mr-3" />
              <h2 className="text-2xl font-semibold">User Safety</h2>
            </div>
            <p className="text-gray-600 mb-6">
              We implement robust safeguards to protect our users:
            </p>
            <ul className="list-disc pl-5 space-y-3 text-gray-600">
              <li>Clear communication about service limitations</li>
              <li>Content filtering for harmful or inappropriate responses</li>
              <li>Regular safety audits and updates</li>
              <li>Proactive identification of inappropriate use cases</li>
            </ul>
          </section>

          {/* Fairness and Bias */}
          <section className="bg-white rounded-xl shadow-md p-6 md:p-8 mb-8">
            <div className="flex items-center mb-6">
              <Users className="w-8 h-8 text-[#B3D9E6] mr-3" />
              <h2 className="text-2xl font-semibold">Fairness and Bias</h2>
            </div>
            <p className="text-gray-600 mb-6">
              We actively work to ensure our AI system:
            </p>
            <ul className="list-disc pl-5 space-y-3 text-gray-600">
              <li>Provides consistent support regardless of user demographics</li>
              <li>Undergoes regular bias testing and correction</li>
              <li>Adapts to individual needs while maintaining fairness</li>
            </ul>
          </section>

          {/* Transparency */}
          <section className="bg-white rounded-xl shadow-md p-6 md:p-8 mb-8">
            <div className="flex items-center mb-6">
              <Brain className="w-8 h-8 text-[#A9D0A1] mr-3" />
              <h2 className="text-2xl font-semibold">AI Transparency</h2>
            </div>
            <p className="text-gray-600 mb-6">
              We maintain clear communication about our AI:
            </p>
            <ul className="list-disc pl-5 space-y-3 text-gray-600">
              <li>Explain capabilities and limitations upfront</li>
              <li>Provide clear information about data usage</li>
              <li>Share regular updates about AI improvements</li>
            </ul>
          </section>

          {/* Limitations */}
          <section className="bg-white rounded-xl shadow-md p-6 md:p-8 mb-8">
            <div className="flex items-center mb-6">
              <AlertCircle className="w-8 h-8 text-[#F4B6A1] mr-3" />
              <h2 className="text-2xl font-semibold">Known Limitations</h2>
            </div>
            <p className="text-gray-600 mb-6">
              We are transparent about what our AI cannot do:
            </p>
            <ul className="list-disc pl-5 space-y-3 text-gray-600">
              <li>Cannot replace professional mental health treatment</li>
              <li>Not appropriate for crisis situations or emergencies</li>
              <li>Cannot provide medical advice or diagnoses</li>
              <li>May not fully understand complex cultural contexts</li>
            </ul>
          </section>
        </div>
      </div>

      {/* Back to Top Button */}
      {showBackToTop && (
        <button
          onClick={scrollToTop}
          className="fixed bottom-8 right-8 bg-[#C2A4D6] hover:bg-[#B390C9] text-white p-3 rounded-full shadow-lg transition-colors"
          aria-label="Back to top"
        >
          <ArrowUp className="w-6 h-6" />
        </button>
      )}
    </div>
  );
};

export default AIEthics;